import * as React from "react";
import { Input, InputProps } from "./Input";

export interface NumericInputProps extends Omit<
  InputProps,
  "onChange" | "value" | "defaultValue"
> {
  value?: number | string | null;
  defaultValue?: number | string | null;
  onChange?: (value: number | null) => void;
  onRawChange?: (value: string | null) => void;
  precision?: number;
  allowNegative?: boolean;
  allowInvalidInput?: boolean;
  preserveRawValue?: boolean;
}

export const NumericInput = React.forwardRef<
  HTMLInputElement,
  NumericInputProps
>(
  (
    {
      value,
      defaultValue,
      onChange,
      onRawChange,
      precision = 2,
      allowNegative = false,
      allowInvalidInput = false,
      preserveRawValue = false,
      onKeyDown,
      onBlur,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [displayValue, setDisplayValue] = React.useState<string>(() => {
      const initial = value ?? defaultValue;
      if (initial === null || initial === undefined || initial === "")
        return "";
      return typeof initial === "number" ? initial.toString() : String(initial);
    });

    // Keep track of the value prop to detect external changes
    const lastPropValue = React.useRef(value);

    React.useEffect(() => {
      // Only sync if the prop value actually changed from the last time we saw it
      if (value !== lastPropValue.current) {
        lastPropValue.current = value;

        // If the user is currently typing, we generally don't want to overwrite their input
        // UNLESS the field was empty or the new value is significantly different (e.g. reset from outside)
        if (isFocused) {
          const numVal =
            value === null || value === undefined || value === ""
              ? null
              : parseFloat(String(value));
          const currentNum =
            displayValue === "" ||
            displayValue === "-" ||
            displayValue === "." ||
            displayValue === "-."
              ? null
              : parseFloat(displayValue);

          // Only overwrite while focused if the field is empty or the change is major (not just a minor sync difference)
          if (
            displayValue === "" ||
            (numVal !== null && numVal !== currentNum)
          ) {
            setDisplayValue(
              value === null || value === undefined || value === ""
                ? ""
                : String(value),
            );
          }
        } else {
          setDisplayValue(
            value === null || value === undefined || value === ""
              ? ""
              : String(value),
          );
        }
      }
    }, [value, isFocused, displayValue]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!allowInvalidInput && ["e", "E", "+"].includes(e.key)) {
        e.preventDefault();
      }
      if (!allowInvalidInput && e.key === "-" && !allowNegative) {
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      const regex =
        allowNegative || allowInvalidInput ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;

      if (allowInvalidInput || val === "" || regex.test(val)) {
        setDisplayValue(val);
        const numeric =
          val === "" || val === "-" || val === "." || val === "-."
            ? null
            : Number(val);
        // Call onChange immediately to keep parent in sync, but the useEffect above will prevent overwriting us
        if (preserveRawValue) {
          onRawChange?.(val === "" ? null : val);
        } else {
          onChange?.(
            numeric !== null && Number.isFinite(numeric) ? numeric : null,
          );
        }
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      e.target.select();
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      const val =
        !preserveRawValue &&
        (displayValue === "-" || displayValue === "." || displayValue === "-.")
          ? ""
          : displayValue;

      // Final normalization on blur
      if (val !== displayValue) {
        setDisplayValue(val);
      }

      const numeric = val === "" ? null : Number(val);
      if (preserveRawValue) {
        onRawChange?.(val === "" ? null : val);
      } else {
        onChange?.(numeric);
      }
      onBlur?.(e);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode={precision === 0 ? "numeric" : "decimal"}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  },
);

NumericInput.displayName = "NumericInput";
